
import * as React from 'react';
import {
  IComponentDecorator,
  IDecoratedComponentType,
  IDecoratedProps,
  ISimpleDecoratedComponentType,
  ISimpleComponentDecorator,
  ISimpleDecoratedProps
} from './Decorators';

/**
 * Props for a component which take React children.
 * @public
 */
export type IReactChildrenProps = Pick<React.Props<{}>, 'children'>;

/**
 * @public
 */
export function injectProps<TProps, TOwnProps = {}>(
  getProps: (props: TOwnProps) => (((props: TOwnProps) => TProps) | TProps)
): IComponentDecorator<TProps, TOwnProps> {
  return <P extends TProps>(component: React.ComponentType<P>): IDecoratedComponentType<P, TProps, TOwnProps> => {
    const Component = component;

    let memoizedGetProps: (props: TOwnProps) => TProps;

    type IProps = IDecoratedProps<P, TProps, TOwnProps>;

    class InjectProps extends React.PureComponent<IProps, TProps> {
      constructor(props: IProps) {
        super(props);

        const getPropsResult = getProps(this.props as Readonly<TOwnProps>);

        if (isMemoizedResult(getPropsResult)) {
          memoizedGetProps = getPropsResult;
          this.state = getPropsResult(this.props as Readonly<TOwnProps>);
        } else {
          memoizedGetProps = getProps as (props: TOwnProps) => TProps;
          this.state = getPropsResult;
        }
      }

      public componentWillReceiveProps(nextProps: IProps): void {
        this.setState(memoizedGetProps(nextProps as Readonly<TOwnProps>));
      }

      public render(): JSX.Element {
        return (
          <Component
            { ...this.props }
            { ...this.state as P }
          />
        );
      }
    }

    return InjectProps;
  };
}

/**
 * @public
 */
export function injectInitialProps<TProps, TOwnProps = {}>(
  getInitialProps: (props: TOwnProps) => TProps
): IComponentDecorator<TProps, TOwnProps> {
  return <P extends TProps>(component: React.ComponentType<P>): IDecoratedComponentType<P, TProps, TOwnProps> => {
    const Component = component;

    type IProps = IDecoratedProps<P, TProps, TOwnProps>;

    class InjectInitialProps extends React.PureComponent<IProps, TProps> {
      constructor(props: IProps) {
        super(props);

        this.state = getInitialProps(this.props as IProps);
      }

      public render(): JSX.Element {
        return (
          <Component
            { ...this.props }
            { ...this.state as P }
          />
        );
      }
    }

    return InjectInitialProps;
  };
}

/**
 * @public
 */
export function wrapWithParent<TOwnProps>(
  parent: React.ComponentType<TOwnProps>
): ISimpleComponentDecorator<TOwnProps> {
  const Parent = parent;

  return <P extends {}>(
    component: React.ComponentType<P>
  ): ISimpleDecoratedComponentType<P, TOwnProps> => {
    const Component = component;

    function WrapWithParent(
      props: ISimpleDecoratedProps<P, TOwnProps> & IReactChildrenProps
    ): JSX.Element {
      return React.createElement(Parent, props, React.createElement(Component, props));
    }

    return WrapWithParent;
  };
}

function isMemoizedResult<TProps, TOwnProps>(
  getPropsResult: ((props: TOwnProps) => TProps) | TProps
): getPropsResult is ((props: TOwnProps) => TProps) {
  return typeof getPropsResult === 'function';
}
